import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import firebaseApp from "../firebaseConfig";
import { AuthContext } from "./AuthContext";
import { ClientContext } from "./ClientContext";
import { apiRequest } from "../api/baseApi";
import { wasAuthFailureReported } from "../api/authFailureMarker";
import { addBreadcrumb, captureError } from "../config/sentry";
import onlineStatusManager from "../utils/onlineStatusManager";

export const CallV3Context = createContext();

// Max remembered ended-call uids (late-duplicate re-ring guard). Exported so
// tests can pin the FIFO eviction behavior.
export const ENDED_CALL_GUARD_MAX = 50;

// FIFO-evict the oldest entries (Set preserves insertion order) so a
// long-lived SPA session can't grow a guard set unboundedly.
const addBounded = (set, value) => {
  set.add(value);
  while (set.size > ENDED_CALL_GUARD_MAX) {
    set.delete(set.values().next().value);
  }
};

export const CallV3Provider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const { selectedClient } = useContext(ClientContext);

  const [callerName, setCallerName] = useState(null);
  const [callId, setCallId] = useState(null);
  const [callEndState, setCallEndState] = useState(null);
  const [callType, setCallType] = useState(null);
  const [inCall, setInCall] = useState(false);
  // GMA-1331: surface popup-blocked / init-failed / network failures to the
  // caller so they aren't left staring at a black screen. `kind` drives the
  // copy + action shown by <CallErrorSnackbar />. `retryUrl` is set when the
  // popup was blocked so the user can re-fire window.open from their click.
  const [placeCallError, setPlaceCallError] = useState(null);
  // Answer errors belong to the ringing call; popup-blocked keeps its
  // "Open call window" recovery.
  const clearAnswerError = useCallback(
    () =>
      setPlaceCallError((prev) =>
        prev?.kind?.startsWith("answer-") ? null : prev,
      ),
    [],
  );
  const onCallEndCallbackRef = useRef(null);

  // #443: do not offer popup retry for a call that already reached a terminal
  // end. busy/offline count as terminal too — retrying the popup for a callee
  // who is busy or offline would open a dead call window under a stale banner.
  useEffect(() => {
    if (
      callEndState === "ended" ||
      callEndState === "rejected" ||
      callEndState === "missed" ||
      callEndState === "cancelled" ||
      callEndState === "busy" ||
      callEndState === "offline"
    ) {
      setPlaceCallError(null);
    }
  }, [callEndState]);

  //AUDIO
  useEffect(() => {
    if (callerName && !inCall && !callEndState) {
      const audio = new Audio(
        "https://cdn.pixabay.com/audio/2021/08/04/audio_f727f1d9fc.mp3"
      );
      audio.loop = true;
      (async () => {
        try {
          await audio.play();
        } catch (error) {
          console.error("Error playing ringtone:", error);
        }
      })();
      return () => {
        audio.pause();
        audio.src = "";
      };
    }
  }, [callerName, inCall, callEndState]);

  // #318: calls whose end this window already processed. Two signals can
  // announce the same teardown — the RTDB calls/<id> removal AND the
  // call_cancelled web push (GMA-1655 / cortex-be#429) — so the teardown
  // below must run at most once per call. Also guards a late/duplicate
  // incoming_call push from re-ringing a call already dealt with here.
  const endedCallUidsRef = useRef(new Set());
  // Capped (addBounded): recent calls stay guarded against late duplicate
  // pushes; ancient ones can't leak.
  const markCallEnded = useCallback((callUid) => {
    addBounded(endedCallUidsRef.current, callUid);
  }, []);
  // Calls whose end was observed (calls/{id} removed, call_cancelled push).
  // Kept apart from endedCallUidsRef, which a failed history fetch releases
  // so teardown can retry: the call has still ended, so it must never be
  // opened or reopened.
  const terminatedCallUidsRef = useRef(new Set());

  // #735: latest call state for the users/{id} listener, which subscribes
  // once per user. answeringRef holds the id of this window's own answer in
  // flight (the backend writes callStatus=active before its response
  // arrives); answeredRef the id of the call this window answered, which
  // stays tracked even when its call window was blocked.
  const callIdRef = useRef(callId);
  const inCallRef = useRef(inCall);
  const answeringRef = useRef(null);
  const answeredRef = useRef(null);
  callIdRef.current = callId;
  inCallRef.current = inCall;

  //INCOMING CALL LISTENER
  useEffect(() => {
    if (!currentUser) return;

    const db = getDatabase(firebaseApp);
    const path = `users/${currentUser.id}`;
    const userRef = ref(db, path);

    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.callUid && data.callStatus === "pending") {
        // A newer call must not inherit the previous call's answer error.
        if (data.callUid !== callIdRef.current) clearAnswerError();
        // Set now, not on the next render: an active write for this call can
        // arrive in the same batch, before callIdRef would otherwise update.
        callIdRef.current = data.callUid;
        setCallId(data.callUid);
        setCallerName(data.callerName);
        setCallType(data.callType || "normal");
        return;
      }
      // #735: the ringing call went active and this window did not answer
      // it, so another device did. Production web push is unavailable
      // (webPushSent:false), so this RTDB write is the signal that reliably
      // reaches the browser. Only "active": an ended/missed call is left to
      // the calls/{id} end listener so its outcome is still shown.
      if (
        data &&
        data.callStatus === "active" &&
        data.callUid &&
        data.callUid === callIdRef.current &&
        !inCallRef.current &&
        answeringRef.current !== data.callUid &&
        answeredRef.current !== data.callUid
      ) {
        addBreadcrumb("call.answer.web_answered_elsewhere", {
          category: "call.lifecycle",
          level: "info",
          callId: data.callUid,
        });
        markCallEnded(data.callUid);
        setCallId(null);
        setCallerName(null);
        setCallType(null);
        // The Answer button is gone, so "press Answer again" no longer
        // applies. popup-blocked keeps its "Open call window" recovery.
        clearAnswerError();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser, markCallEnded, clearAnswerError]);

  // Shared teardown for a call that ended remotely (caller cancelled,
  // answered elsewhere, timed out). Fetches the authoritative outcome from
  // call_history so MissedCallModal shows the right copy — the exact
  // behavior the RTDB call-end listener always had, now also reachable from
  // the call_cancelled web push.
  const handleRemoteCallEnd = useCallback(async (endedCallId) => {
    if (!endedCallId || endedCallUidsRef.current.has(endedCallId)) return;
    // Mark as in-progress so concurrent signals don't double-run teardown, but
    // release the guard on any failure/non-ok so a later signal can retry — a
    // network/auth error must not permanently wedge the UI in a ringing state.
    markCallEnded(endedCallId);
    addBounded(terminatedCallUidsRef.current, endedCallId);
    // The ended call's blocked-window recovery has nothing left to open,
    // whether or not its history loads.
    if (answeredRef.current === endedCallId) {
      setPlaceCallError((prev) =>
        prev?.kind === "popup-blocked" ? null : prev,
      );
    }
    try {
      const historyResponse = await apiRequest(`call_history/${endedCallId}`, {
        method: "GET",
      });
      if (!historyResponse.ok) {
        endedCallUidsRef.current.delete(endedCallId);
        return;
      }
      const history = await historyResponse.json();
      if (history.status !== "busy") {
        setCallEndState(history.status);
      }
      setInCall(false);
    } catch (error) {
      endedCallUidsRef.current.delete(endedCallId);
      addBreadcrumb("call.push.web_remote_end_failed", {
        category: "call.lifecycle",
        level: "warning",
        callId: endedCallId,
        error: error?.message || String(error),
      });
    }
  }, [markCallEnded]);

  //CALL END LISTENER
  useEffect(() => {
    if (!callId || callId === 0) return;
    const db = getDatabase(firebaseApp);
    const path = `calls/${callId}`;
    const callRef = ref(db, path);
    const unsubscribe = onValue(callRef, async (snapshot) => {
      if (!snapshot.exists()) {
        await handleRemoteCallEnd(callId);
      }
    });
    return () => {
      unsubscribe();
    };
  }, [callId, handleRemoteCallEnd]);

  // GMA-1331: clear any prior error so the snackbar disappears once the
  // caller successfully starts a new call attempt.
  const clearPlaceCallError = () => setPlaceCallError(null);

  const openCall = useCallback((callIdToOpen, preopened = null) => {
    const url = `${window.location.origin}/call?callId=${encodeURIComponent(callIdToOpen)}`;
    // Prefer a window opened synchronously in the click gesture (placeCall /
    // answerCall); after awaits, Safari/Firefox often return null from
    // window.open even when the user clicked — which used to raise a false
    // "popup blocked" banner. NB: never pass "noopener"/"noreferrer" — that
    // also returns null on success. Anti-tabnabbing: null opener manually.
    // Call window.open directly (not a helper closed over this callback) so
    // react-hooks/exhaustive-deps stays clean under lint:strict.
    let popup =
      preopened && !preopened.closed ? preopened : null;
    if (popup) {
      try {
        popup.location = url;
      } catch (_err) {
        // Cross-origin about:blank navigation can throw in some embeds;
        // fall through to a fresh open below.
        popup = null;
      }
    }
    if (!popup) {
      popup = window.open(url, "_blank");
    }
    const popupBlocked = !popup;
    addBreadcrumb("call.openCall", {
      category: "call.lifecycle",
      level: popupBlocked ? "warning" : "info",
      callId: callIdToOpen,
      popupBlocked,
      url,
      reusedPreopened: Boolean(preopened) && !preopened.closed,
    });
    if (popup) {
      popup.opener = null;
      popup.focus();
      setPlaceCallError(null);
      return true;
    }
    // GMA-1331: surface popup-blocked so the user has a one-click recovery
    // path. The retry must be invoked from a user gesture (button click)
    // so the browser allows the new window.
    setPlaceCallError({
      kind: "popup-blocked",
      message:
        "Your browser blocked the call window. Allow popups for this site or click below to open it.",
      retryUrl: url,
    });
    return false;
  }, []);

  const placeCall = useCallback(async () => {
    const receiverId = selectedClient?.id;
    setPlaceCallError(null);
    // FE-1E (#187): MissedCallModal "Call back" can invoke placeCall with no
    // selected client; guard the receiver deref so we never throw, and treat it
    // as a benign no-op (no error snackbar). GMA-1331 error-surfacing below.
    if (!receiverId) {
      addBreadcrumb("placeCall: no selected client to call", {
        category: "call.lifecycle.place_call.no_receiver",
        level: "warning",
        hasSelectedClient: Boolean(selectedClient),
      });
      return;
    }
    addBreadcrumb("call.placeCall", {
      category: "call.lifecycle",
      level: "info",
      stage: "start",
      receiverId,
      callType: "normal",
    });
    // Open inside the click's user-gesture window — after the awaits below
    // the gesture has lapsed and browsers return null even though the user
    // clicked. Navigate the placeholder once call/init resolves; close it on
    // 409/error so no orphan about:blank tab remains.
    const preopened = window.open("about:blank", "_blank");
    let response;
    try {
      response = await apiRequest("call/init", {
        method: "POST",
        body: { receiverId, callType: "normal" },
        version: "v3",
      });
    } catch (err) {
      if (preopened && !preopened.closed) preopened.close();
      const isLikelyNetworkFailure =
        err instanceof TypeError ||
        (err && typeof err.message === "string" &&
          /failed to fetch|network|load failed/i.test(err.message));
      addBreadcrumb("call.placeCall", {
        category: "call.lifecycle",
        level: "error",
        stage: isLikelyNetworkFailure ? "network-error" : "request-error",
        receiverId,
      });
      captureError(err, {
        tags: {
          feature: "call.placeCall",
          failure: isLikelyNetworkFailure ? "network" : "unexpected",
        },
        extra: { receiverId },
      });
      setPlaceCallError({
        kind: isLikelyNetworkFailure ? "network" : "init-failed",
        message: isLikelyNetworkFailure
          ? "Couldn't start call — network problem. Check your connection and try again."
          : "Couldn't start call — an unexpected error occurred. Please try again.",
      });
      return;
    }
    if (response.ok) {
      const data = await response.json();
      if (data.callId) {
        setCallId(data.callId);
        const opened = openCall(
          data.callId,
          preopened && !preopened.closed ? preopened : null,
        );
        if (opened) {
          addBreadcrumb("call.placeCall", {
            category: "call.lifecycle",
            level: "info",
            stage: "ok",
            receiverId,
            callId: data.callId,
          });
        } else if (preopened && !preopened.closed) {
          preopened.close();
        }
        return;
      }
      if (preopened && !preopened.closed) preopened.close();
      // GMA-1331 (Copilot #171): ok without callId is still an init failure —
      // otherwise placeCall silently returns and the caregiver sees no window.
      addBreadcrumb("call.placeCall", {
        category: "call.lifecycle",
        level: "error",
        stage: "init-missing-call-id",
        receiverId,
      });
      captureError(new Error("call/init returned ok but missing callId"), {
        tags: { feature: "call.placeCall", failure: "init-missing-call-id" },
        extra: { receiverId },
      });
      setPlaceCallError({
        kind: "init-failed",
        message:
          "Couldn't start call — the server didn't return a call ID. Please try again.",
      });
      return;
    }
    if (response.status === 409) {
      if (preopened && !preopened.closed) preopened.close();
      const errorData = await response.json();
      setCallId(0);
      setCallEndState(
        errorData.ended_status || errorData.userStatus || "busy"
      );
      return;
    }
    if (preopened && !preopened.closed) preopened.close();
    addBreadcrumb("call.placeCall", {
      category: "call.lifecycle",
      level: "error",
      stage: "init-failed",
      receiverId,
      status: response.status,
    });
    captureError(new Error(`call/init returned ${response.status}`), {
      tags: { feature: "call.placeCall", failure: "init-non2xx" },
      extra: { receiverId, status: response.status },
    });
    setPlaceCallError({
      kind: "init-failed",
      message: `Couldn't start call — server returned ${response.status}. Please try again.`,
    });
  }, [openCall, selectedClient]);

  // #808: every way an answer can fail is shown to the caregiver through
  // placeCallError (CallErrorSnackbar). Production logged four answer POSTs
  // failing in the browser with nothing on screen. Resolves rather than
  // rejects: CallReceiver calls this straight from onClick.
  const answerCall = async () => {
    const answeringCallId = callId;
    // A double-click would send a second answer whose 409 tears down the
    // call the first one just connected. Only the same call: a newer call
    // can still be answered while an old answer is in flight.
    if (answeringRef.current !== null && answeringRef.current === answeringCallId) {
      return null;
    }
    // Already answered here and only the call window is missing (blocked
    // popup): open it in this click instead of answering again, whose 409
    // would tear down the live call.
    if (answeredRef.current !== null && answeredRef.current === answeringCallId) {
      // Never reopen a call that has since ended.
      if (
        endedCallUidsRef.current.has(answeringCallId) ||
        terminatedCallUidsRef.current.has(answeringCallId)
      ) {
        return null;
      }
      addBreadcrumb("call.answerCall", {
        category: "call.lifecycle",
        level: "info",
        stage: "reopen",
        callId: answeringCallId,
      });
      if (openCall(answeringCallId)) {
        inCallRef.current = true;
        setInCall(true);
      }
      return null;
    }
    // Same gesture-window rule as placeCall: open before the await.
    const preopened = window.open("about:blank", "_blank");
    const closePlaceholder = () => {
      if (preopened && !preopened.closed) preopened.close();
    };
    answeringRef.current = answeringCallId;
    // Still the ringing call, and not ended while the answer was in flight.
    const isStillCurrent = () =>
      callIdRef.current === answeringCallId &&
      !endedCallUidsRef.current.has(answeringCallId) &&
      !terminatedCallUidsRef.current.has(answeringCallId);
    setPlaceCallError(null);
    addBreadcrumb("call.answerCall", {
      category: "call.lifecycle",
      level: "info",
      stage: "start",
      callId: answeringCallId,
    });
    try {
      let response;
      try {
        response = await apiRequest("call/answer", {
          method: "POST",
          body: {
            callId: answeringCallId,
            // #735: lets the backend's answered-elsewhere fan-out run,
            // excluding this browser and tearing down the caregiver's phones.
            deviceId: onlineStatusManager.getOrCreateDeviceId(),
          },
          version: "v3",
        });
      } catch (err) {
        closePlaceholder();
        const isLikelyNetworkFailure =
          Boolean(err?.isNetworkFailure) ||
          err instanceof TypeError ||
          (err && typeof err.message === "string" &&
            /failed to fetch|network|load failed/i.test(err.message));
        addBreadcrumb("call.answerCall", {
          category: "call.lifecycle",
          level: "error",
          stage: isLikelyNetworkFailure ? "network-error" : "request-error",
          callId: answeringCallId,
        });
        // apiRequest has already reported these, with its own suppression of
        // session ends and transport bursts.
        if (!err?.isNetworkFailure && !err?.isAuthFailure) {
          captureError(err, {
            tags: {
              feature: "call.answerCall",
              failure: isLikelyNetworkFailure ? "network" : "unexpected",
            },
            extra: { callId: answeringCallId },
          });
        }
        // Another call may be ringing by now, or this one ended; the error
        // is no longer actionable.
        if (!isStillCurrent()) return null;
        setPlaceCallError(
          isLikelyNetworkFailure
            ? {
                kind: "answer-network",
                message:
                  "Couldn't answer the call — network problem. Check your connection and press Answer again.",
              }
            : {
                kind: "answer-failed",
                message:
                  "Couldn't answer the call — an unexpected error occurred. Press Answer to try again.",
              },
        );
        return null;
      }

      if (response.ok) {
        // The call ended while the answer was in flight, or a newer call is
        // ringing (so this one ended): opening it would show a dead call or
        // hide the newer call's modal.
        if (!isStillCurrent()) {
          closePlaceholder();
          addBreadcrumb("call.answerCall", {
            category: "call.lifecycle",
            level: "warning",
            stage: "stale",
            callId: answeringCallId,
          });
          return response;
        }
        answeredRef.current = answeringCallId;
        // Ignore a placeholder the user closed while answer was in flight.
        const opened = openCall(
          answeringCallId,
          preopened && !preopened.closed ? preopened : null,
        );
        if (opened) {
          // Update the ref now, not on the next render: answeringRef clears
          // in `finally` below, and an RTDB active write arriving before the
          // re-render must not read this window as "answered elsewhere".
          inCallRef.current = true;
          setInCall(true);
          addBreadcrumb("call.answerCall", {
            category: "call.lifecycle",
            level: "info",
            stage: "ok",
            callId: answeringCallId,
          });
        } else {
          // openCall already surfaced popup-blocked; close the about:blank
          // placeholder so we don't leave an orphan tab or mark in-call.
          closePlaceholder();
        }
        return response;
      }

      closePlaceholder();
      if (response.status === 409) {
        // Already answered, ended or timed out: nothing left to answer, so
        // stop ringing instead of leaving a modal whose button cannot work.
        addBreadcrumb("call.answerCall", {
          category: "call.lifecycle",
          level: "warning",
          stage: "conflict",
          callId: answeringCallId,
        });
        const current = isStillCurrent();
        // A call whose end was already observed keeps its teardown state:
        // its id for the post-call note, and a history fetch it may retry.
        if (!terminatedCallUidsRef.current.has(answeringCallId)) {
          markCallEnded(answeringCallId);
        }
        // A newer call may be ringing, or this one already ended: never
        // dismiss either over the old answer's 409.
        if (!current) return response;
        setCallId(null);
        setCallerName(null);
        setCallType(null);
        setPlaceCallError({
          kind: "answer-unavailable",
          message: "This call was already answered or has ended.",
        });
        return response;
      }

      addBreadcrumb("call.answerCall", {
        category: "call.lifecycle",
        level: "error",
        stage: "answer-failed",
        callId: answeringCallId,
        status: response.status,
      });
      // apiRequest reports every 5xx itself, and a 401 whose silent refresh
      // failed (it marks that response).
      if (response.status < 500 && !wasAuthFailureReported(response)) {
        captureError(new Error(`call/answer returned ${response.status}`), {
          tags: { feature: "call.answerCall", failure: "answer-non2xx" },
          extra: { callId: answeringCallId, status: response.status },
        });
      }
      if (!isStillCurrent()) return response;
      setPlaceCallError({
        kind: "answer-failed",
        message: `Couldn't answer the call — server returned ${response.status}. Press Answer to try again.`,
      });
      return response;
    } finally {
      // A newer answer may own the guard by now.
      if (answeringRef.current === answeringCallId) answeringRef.current = null;
    }
  };

  const endCall = async () => {
    return apiRequest("call/end", {
      method: "POST",
      body: {
        callId,
      },
      version: "v3",
    });
  };

  // #318 (companion to cortex-be#429): the service worker forwards
  // incoming_call / call_cancelled web pushes and notification Answer/Decline
  // clicks to open windows via postMessage. Everything routes into the
  // existing CallV3 state and flows — the push is just a second signal source
  // next to the Firebase RTDB listeners, deduped by callUid.
  //
  // Latest-ref pattern: one listener registered for the provider's lifetime,
  // dispatching to a closure rebuilt each render so it always sees fresh
  // state (callId/inCall/answerCall) without re-subscribing on every change.
  const swMessageHandlerRef = useRef(null);
  // useLayoutEffect (not useEffect): the ref must point at the fresh closure
  // synchronously at commit, before the browser can dispatch queued SW
  // message events to a stale one (back-to-back incoming_call/call_cancelled).
  useLayoutEffect(() => {
    swMessageHandlerRef.current = (message) => {
      if (!message || typeof message !== "object" || !message.callUid) return;

      if (message.type === "incoming_call") {
        if (inCall) return; // never clobber an active call
        if (endedCallUidsRef.current.has(message.callUid)) return; // late duplicate of a handled call
        if (callId === message.callUid) {
          // RTDB pending signal already rang this call — push loses.
          addBreadcrumb("call.push.web_incoming_deduped", {
            category: "call.lifecycle",
            level: "info",
            callId: message.callUid,
          });
          return;
        }
        addBreadcrumb("call.push.web_incoming_received", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
          callType: message.callType || "normal",
        });
        // Same as the RTDB pending path: an active write for this call can
        // land before the next render updates callIdRef.
        clearAnswerError();
        callIdRef.current = message.callUid;
        setCallId(message.callUid);
        setCallerName(message.callerName || "Someone");
        setCallType(message.callType || "normal");
        return;
      }

      if (message.type === "call_cancelled") {
        if (callId !== message.callUid || inCall) return;
        addBreadcrumb("call.push.web_cancel_received", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
          reason: message.reason || "",
        });
        // handleRemoteCallEnd is failure-safe internally (releases its dedupe
        // guard and breadcrumbs on error), so it can never reject — void marks
        // this as intentional fire-and-forget from the SW-message handler.
        void handleRemoteCallEnd(message.callUid);
        return;
      }

      if (message.type === "incoming_call_answer") {
        if (callId !== message.callUid || inCall || callEndState) return;
        addBreadcrumb("call.push.web_notification_answer", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
        });
        void answerCall().catch((error) => {
          addBreadcrumb("call.push.web_notification_answer_failed", {
            category: "call.lifecycle",
            level: "warning",
            callId: message.callUid,
            error: error?.message || String(error),
          });
        });
        return;
      }

      if (message.type === "incoming_call_decline") {
        if (callId !== message.callUid || inCall || callEndState) return;
        addBreadcrumb("call.push.web_notification_decline", {
          category: "call.lifecycle",
          level: "info",
          callId: message.callUid,
        });
        // Same flow as CallReceiver's Reject button; mark the call ended so
        // a late duplicate incoming_call push cannot re-ring it, but release
        // the guard if endCall fails so later teardown signals (RTDB removal
        // / call_cancelled push) can still retry — same pattern as
        // handleRemoteCallEnd above.
        markCallEnded(message.callUid);
        endCall()
          .then(() => clearEndCallState())
          .catch((error) => {
            endedCallUidsRef.current.delete(message.callUid);
            addBreadcrumb("call.push.web_notification_decline_failed", {
              category: "call.lifecycle",
              level: "warning",
              callId: message.callUid,
              error: error?.message || String(error),
            });
          });
      }
    };
  });

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }
    const serviceWorkerContainer = navigator.serviceWorker;
    const onSwMessage = (event) => {
      if (swMessageHandlerRef.current) {
        swMessageHandlerRef.current(event.data);
      }
    };
    serviceWorkerContainer.addEventListener("message", onSwMessage);
    return () => {
      serviceWorkerContainer.removeEventListener("message", onSwMessage);
    };
  }, []);

  const submitCallDescription = async (description) => {
    const response = await apiRequest(`call_histories/${callId}`, {
      method: "PUT",
      body: { description },
    });
    return response;
  };

  const registerOnCallEnd = useCallback((callback) => {
    onCallEndCallbackRef.current = callback;
  }, []);

  const clearEndCallState = () => {
    if (onCallEndCallbackRef.current) {
      onCallEndCallbackRef.current();
    }
    setCallEndState(null);
    setCallId(null);
    setCallerName(null);
    setCallType(null);
    // Reject dismisses the ringing call, so "press Answer again" no longer
    // applies.
    clearAnswerError();
  };

  return (
    <CallV3Context.Provider
      value={{
        callId,
        callEndState,
        callerName,
        callType,
        inCall,
        placeCall,
        answerCall,
        endCall,
        openCall,
        submitCallDescription,
        clearEndCallState,
        registerOnCallEnd,
        placeCallError,
        clearPlaceCallError,
      }}
    >
      {children}
    </CallV3Context.Provider>
  );
};
